import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { CgMenuLeft } from 'react-icons/cg';
import Breadcrumbs from '../../components/Breadcrumbs';
import Notification from '../../components/Notification';
import Stats from '../../components/Stats';
import { fetchTripOverview, tripLists, setSelectedTripId } from '../../store/Slices/TripSlice';
import "../../styles/Trip.css";
import "../../styles/Access.css";
import "../../styles/Services.css";
import { activeStatsIcon, lateStatsIcon, plusIcon, statsCompletedIcon, tripStatsIcon } from '../../utils/svgs';
import TripLists from './TripLists';
import TripMap from './TripMap';
import TripTrackingMap from './TripTrackingMap';
import TripsList from './TripsList/TripsList';
import CreateTripDrawer from './TripsList/CreateTripDrawer';
import { useTripListingExcelExport, useTripListingPdfExport } from './TripsList/useTripListingPdfExport';
import PermissionWrapper from '../../components/PermissionWrapper';
import { AppMainModules, TripPermission, TripSubModules } from '../../utils/app-constant';
import ExportButton from '../../components/ExportButton';
import { exportDataIcon } from '../../utils/svgs';

const Trips = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const outletContext = useOutletContext();
    const toggleMobileSidebar = outletContext?.toggleMobileSidebar;

    const activeTab = searchParams.get("tab") === "listing" ? "listing" : "tracking";

    const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [mobileMapOpen, setMobileMapOpen] = useState(false);

    // Lifted up from TripsList so this page's own header (TripsList is embedded with
    // hideHeader=true here) can export reports using the same active filters.
    const [listingSearchValue, setListingSearchValue] = useState("");
    const [listingSearchFilters, setListingSearchFilters] = useState({});
    const {
        hasActiveFilters: hasActiveListingFilters,
        loading: pdfExportLoading,
        handleExport: handleExportListingPdf,
    } = useTripListingPdfExport(listingSearchValue, listingSearchFilters);
    const {
        loading: excelExportLoading,
        handleExport: handleExportListingExcel,
    } = useTripListingExcelExport(listingSearchValue, listingSearchFilters);
    const listingExportLoading = pdfExportLoading || excelExportLoading;

    useEffect(() => {
        if (searchParams.get("create") === "true") {
            setIsCreateDrawerOpen(true);
        }
    }, [searchParams]);

    const handleTabChange = (tab) => {
        const nextParams = new URLSearchParams(searchParams);
        if (tab === "listing") {
            nextParams.set("tab", "listing");
        } else {
            nextParams.delete("tab");
        }
        setSearchParams(nextParams);
    };

    const handleCloseCreateDrawer = () => {
        setIsCreateDrawerOpen(false);
        if (searchParams.get("create")) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("create");
            setSearchParams(nextParams, { replace: true });
        }
    };

    const handleTripCreated = () => {
        handleCloseCreateDrawer();
        dispatch(fetchTripOverview());
        dispatch(tripLists({ status: "all", activeTab: "all", page: 1, limit: 10 }));
        setRefreshTrigger((prev) => prev + 1);
    };

    const { tripOverviewData } = useSelector((state) => state.trips);

    useEffect(() => {
        dispatch(fetchTripOverview());
    }, [dispatch]);

    const tripStatsData = [
        {
            title: "Total Trips",
            value: tripOverviewData?.totalTrips?.total_trips || "0",
            text: (
                <>
                    <span>Last updated: </span>
                    <b>
                        {tripOverviewData?.totalTrips?.lastUpdated
                            ? new Date(
                                tripOverviewData?.totalTrips?.lastUpdated
                            ).toLocaleDateString()
                            : "--"}
                    </b>
                </>
            ),
            icon: tripStatsIcon,
        },
        {
            title: "New",
            value: tripOverviewData?.newTrips?.total || "0",
            text: (
                <>
                    <b>{tripOverviewData?.newTrips?.info}% </b>
                    <span>from total</span>
                </>
            ),
            icon: activeStatsIcon,
        },
        {
            title: "In Transit",
            value: tripOverviewData?.inTransitTrips?.total || "0",
            text: (
                <>
                    <b>{tripOverviewData?.inTransitTrips?.info}% </b>
                    <span>from total</span>
                </>
            ),
            icon: activeStatsIcon,
        },
        {
            title: "Completed",
            value: tripOverviewData?.completeTrips?.total || "0",
            text: (
                <>
                    <b>{tripOverviewData?.completeTrips?.info}% </b>
                    <span>from total</span>
                </>
            ),
            icon: statsCompletedIcon,
            className: "completed",
        },
        {
            title: "Closed",
            value: tripOverviewData?.closedTrips?.total || "0",
            text: (
                <>
                    <b>{tripOverviewData?.closedTrips?.info}% </b>
                    <span>from total</span>
                </>
            ),
            icon: lateStatsIcon,
            className: "late",
        },
    ];

    return (
        <div className={`trip_page ${activeTab === "listing" ? "trip_page_listing" : "trip_page_tracking"}`}>

            {/* ── Mobile Header ── */}
            <div className="trips_mobile_header">
                <div className="trips_mobile_header_left">
                    <button className="trips_mobile_menu_btn" onClick={toggleMobileSidebar}>
                        <CgMenuLeft size={22} color="#1c1c1c" />
                    </button>
                    <h2 className="trips_mobile_title">Trip Management</h2>
                </div>
                <div className="trips_mobile_header_right">
                    <PermissionWrapper
                        permission={TripPermission.Create}
                        module={TripSubModules.Trips}
                        mainModule={AppMainModules.tripManagement}
                    >
                        <button className="trips_mobile_create_btn" onClick={() => setIsCreateDrawerOpen(true)}>+ Create</button>
                    </PermissionWrapper>
                </div>
            </div>

            {/* ── Mobile Tabs Row ── */}
            <div className="trips_mobile_tabs_row">
                <div className="trips_mobile_tab_btns">
                    <button
                        className={activeTab === "tracking" ? "active" : ""}
                        onClick={() => handleTabChange("tracking")}
                    >Tracking</button>
                    <button
                        className={activeTab === "listing" ? "active" : ""}
                        onClick={() => handleTabChange("listing")}
                    >Listing</button>
                </div>
            </div>

            {/* ── Mobile Export Buttons Row (Listing only) ── */}
            {activeTab === "listing" && (
                <div className="trips_mobile_export_row">
                    <ExportButton
                        type="pdf"
                        label="PDF Download"
                        onClick={handleExportListingPdf}
                        disabled={!hasActiveListingFilters || listingExportLoading}
                        loading={pdfExportLoading}
                        hasActiveFilters={hasActiveListingFilters}
                    />
                    <ExportButton
                        type="excel"
                        label="Excel Download"
                        onClick={handleExportListingExcel}
                        disabled={!hasActiveListingFilters || listingExportLoading}
                        loading={excelExportLoading}
                        hasActiveFilters={hasActiveListingFilters}
                    />
                </div>
            )}

            {/* ── Desktop Header ── */}
            <Breadcrumbs values={["Trip Management", activeTab === "tracking" ? "Tracking" : "Listing"]} />

            <div className='access_view trips_desktop_header'>
                <div className="d-flex align-items-center gap-3">
                    <h2>Trip Management</h2>

                    <div className="access_btn">
                        <button
                            type="button"
                            className={activeTab === "tracking" ? "active" : ""}
                            onClick={() => handleTabChange("tracking")}
                        >
                            Tracking
                        </button>
                        <button
                            type="button"
                            className={activeTab === "listing" ? "active" : ""}
                            onClick={() => handleTabChange("listing")}
                        >
                            Listing
                        </button>
                    </div>
                </div>

                <div className='services_buttons'>
                    <Notification />

                    {activeTab === "listing" && (
                        <ExportButton
                            type="pdf"
                            onClick={handleExportListingPdf}
                            disabled={!hasActiveListingFilters || listingExportLoading}
                            loading={pdfExportLoading}
                            hasActiveFilters={hasActiveListingFilters}
                        />
                    )}

                    {activeTab === "listing" && (
                        <ExportButton
                            type="excel"
                            onClick={handleExportListingExcel}
                            disabled={!hasActiveListingFilters || listingExportLoading}
                            loading={excelExportLoading}
                            hasActiveFilters={hasActiveListingFilters}
                        />
                    )}

                    <PermissionWrapper
                        permission={TripPermission.Create}
                        module={TripSubModules.Trips}
                        mainModule={AppMainModules.tripManagement}
                    >
                        <button onClick={() => setIsCreateDrawerOpen(true)}>{plusIcon} Create New Trip</button>
                    </PermissionWrapper>
                </div>
            </div>

            {activeTab === "tracking" && (
                <>
                    <Stats statsData={tripStatsData} />

                    <div className="page_container">
                        <Row className="trip_layout_row">
                            <Col md={3} xs={12} className="trip_list_col">
                                <div className="trip_list_scroll">
                                    <TripLists
                                        onOpenCreateTrip={() => setIsCreateDrawerOpen(true)}
                                        onMobileCardSelect={() => setMobileMapOpen(true)}
                                    />
                                </div>
                            </Col>
                            <Col md={9} xs={12} className="trip_map_col">
                                <TripTrackingMap />
                            </Col>
                        </Row>
                    </div>

                    {/* ── Mobile Map Overlay ── */}
                    {mobileMapOpen && (
                        <div className="mobile_map_overlay">
                            <div className="mobile_map_overlay_header">
                                <span className="mobile_map_overlay_title">Trip Map</span>
                                <button
                                    className="mobile_map_close_btn"
                                    onClick={() => {
                                        setMobileMapOpen(false);
                                        dispatch(setSelectedTripId(null));
                                    }}
                                >✕ Close</button>
                            </div>
                            <div className="mobile_map_overlay_body">
                                <TripTrackingMap />
                            </div>
                        </div>
                    )}
                </>
            )}

            {activeTab === "listing" && (
                <TripsList
                    hideHeader={true}
                    refreshTrigger={refreshTrigger}
                    searchValue={listingSearchValue}
                    setSearchValue={setListingSearchValue}
                    searchFilters={listingSearchFilters}
                    setSearchFilters={setListingSearchFilters}
                />
            )}

            <CreateTripDrawer
                isOpen={isCreateDrawerOpen}
                onClose={handleCloseCreateDrawer}
                onTripCreated={handleTripCreated}
            />
        </div>
    );
};

export default Trips;
